import React, { useState } from 'react';
import {
  CheckCircle2,
  WifiOff,
  AlertOctagon,
  Printer,
  FileText,
  History,
  RefreshCw,
  Tag,
  Package
} from 'lucide-react';
import { OABLineItem, OABSubOrder } from '../types/oab';
import { ProjectLabelItem } from './PrintSheetProjectLabels';
import { OABStageNotifyPanel } from './OABStageNotifyPanel';

export interface ReceptionSuccessNriPanelProps {
  receptionComplete: any;
  selectedFolio: string;
  activeSubOrderFolio: string | null;
  subOrdenesList: OABSubOrder[];
  pendingOrders: any[];
  itemsToReceive: OABLineItem[];
  labelsToPrint: ProjectLabelItem[];
  notaEntrega: string;
  choferNombre?: string;
  placaVehiculo?: string;
  storesSummary?: string;
  nriPrinted: boolean;
  rejectionPrinted: boolean;
  onSelectSubOrderFromSuccess: (folio: string, numeroCotizacion?: string) => void;
  onPrintNriEntry: () => void;
  onPrintRejectionAct: () => void;
  onOpenHistoryTab: () => void;
  onTriggerRequote: (targetRequote: OABLineItem[], folio: string) => void;
  onOpenLabelModal: () => void;
  onClose: () => void;
}

export const ReceptionSuccessNriPanel: React.FC<ReceptionSuccessNriPanelProps> = ({
  receptionComplete,
  selectedFolio,
  activeSubOrderFolio,
  subOrdenesList,
  pendingOrders,
  itemsToReceive,
  labelsToPrint,
  notaEntrega,
  choferNombre,
  placaVehiculo,
  storesSummary,
  nriPrinted,
  rejectionPrinted,
  onSelectSubOrderFromSuccess,
  onPrintNriEntry,
  onPrintRejectionAct,
  onOpenHistoryTab,
  onTriggerRequote,
  onOpenLabelModal,
  onClose
}) => {
  const [showPrevCompleted, setShowPrevCompleted] = useState(false);
  const [showCanceledMgmt, setShowCanceledMgmt] = useState(false);

  const activeVendor =
    receptionComplete?.nriSnapshot?.proveedor ||
    subOrdenesList.find(s => s.folio === activeSubOrderFolio)?.proveedor ||
    pendingOrders.find(o => o.folio === selectedFolio)?.proveedor ||
    '';

  const resolvedStoresSummary =
    storesSummary ||
    receptionComplete?.nriSnapshot?.tiendaDestino ||
    itemsToReceive.find(it => it.proyectoNombre)?.proyectoNombre ||
    'Stock General Almacén';

  return (
    <div className="p-6 text-center space-y-4 max-w-2xl mx-auto my-auto overflow-y-auto max-h-full">
      <div
        className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto border ${
          receptionComplete.status === 'queued_offline'
            ? 'bg-amber-500/20 text-amber-400 border-amber-500/40'
            : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
        }`}
      >
        {receptionComplete.status === 'queued_offline' ? (
          <WifiOff className="w-7 h-7" />
        ) : (
          <CheckCircle2 className="w-8 h-8" />
        )}
      </div>

      <div>
        <h3 className="text-lg font-bold text-slate-100">
          {receptionComplete.status === 'queued_offline'
            ? '¡Recepción Guardada en Cola Offline!'
            : '¡Recepción Asentada Exitosamente!'}
        </h3>
        <p className="text-xs text-slate-400 mt-1">
          {receptionComplete.message ||
            'Los materiales han ingresado formalmente a las existencias físicas y a BD_Recepciones_Rampa.'}
        </p>
      </div>

      <div className="bg-page border border-borderSubtle rounded-lg p-4 text-xs text-left space-y-1.5 font-mono">
        {receptionComplete.nriFolio && (
          <div className="flex justify-between">
            <span className="text-slate-500">Folio NRI:</span>
            <span className="text-emerald-400 font-black">{receptionComplete.nriFolio}</span>
          </div>
        )}
        {receptionComplete.actaRechazoFolio && (
          <div className="flex justify-between">
            <span className="text-slate-500">Acta de Rechazo:</span>
            <span className="text-rose-400 font-bold">{receptionComplete.actaRechazoFolio}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-slate-500">Folio OAB:</span>
          <span className="text-brand-400 font-bold">{receptionComplete.folioOAB}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Destino / Pedido:</span>
          <span className="text-sky-300 font-semibold truncate max-w-[320px]">{resolvedStoresSummary}</span>
        </div>
        {(choferNombre || placaVehiculo || receptionComplete.nriSnapshot?.choferNombre || receptionComplete.nriSnapshot?.placaVehiculo) && (
          <div className="flex justify-between">
            <span className="text-slate-500">Transporte / Placa:</span>
            <span className="text-slate-200">
              {[
                choferNombre || receptionComplete.nriSnapshot?.choferNombre,
                (placaVehiculo || receptionComplete.nriSnapshot?.placaVehiculo)
                  ? `[${placaVehiculo || receptionComplete.nriSnapshot?.placaVehiculo}]`
                  : ''
              ]
                .filter(Boolean)
                .join(' ')}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-slate-500">Estado:</span>
          <span className="text-slate-200">{receptionComplete.estadoGeneral || 'Completada'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Ítems Procesados:</span>
          <span className="text-slate-200">{receptionComplete.itemsProcesados}</span>
        </div>
        {receptionComplete.duplicateDetected && (
          <div className="text-amber-400 text-[11px] pt-1">
            ⚠️ Protección de Idempotencia: Esta orden ya contaba con registro previo para la misma nota.
          </div>
        )}
      </div>

      {/* Cinta de Cuadratura Integral 360° de la OAB — 5 Partes (PLAN-055) */}
      {receptionComplete.nriSnapshot && (() => {
        const snap = receptionComplete.nriSnapshot;
        const tot = snap.totales || {};
        const prevComp = snap.itemsCompletadosPrevios || [];
        const cancMgmt = snap.itemsCanceladosGerencia || [];
        const totalOabLines =
          tot.renglonesTotalesOAB ??
          ((snap.itemsRecibidos?.length || 0) +
            (snap.itemsFaltantes?.length || 0) +
            prevComp.length +
            cancMgmt.length);

        return (
          <div className="bg-page border border-borderSubtle rounded-lg p-3.5 text-left space-y-2.5">
            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-slate-400">
              <span className="font-bold">Cuadratura Integral 360° ({totalOabLines} renglones OAB)</span>
              <span>{snap.estadoGeneralOAB || receptionComplete.estadoGeneral}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 text-[10px] font-mono">
              <div className="p-2 rounded bg-emerald-950/30 border border-emerald-500/30">
                <div className="text-emerald-300/80">✅ Recibidos Hoy</div>
                <div className="font-bold text-emerald-400">
                  {snap.itemsRecibidos?.length || 0} ({tot.recibidosHoy ?? 0}u)
                </div>
              </div>
              <div className="p-2 rounded bg-amber-950/30 border border-amber-500/30">
                <div className="text-amber-300/80">⏳ Backorder</div>
                <div className="font-bold text-amber-400">
                  {snap.itemsFaltantes?.length || 0} ({tot.pendientesBackorder ?? 0}u)
                </div>
              </div>
              <div className="p-2 rounded bg-rose-950/30 border border-rose-500/30">
                <div className="text-rose-300/80">❌ Rechazados</div>
                <div className="font-bold text-rose-400">
                  {snap.itemsRechazados?.length || 0} ({tot.rechazadosHoy ?? 0}u)
                </div>
              </div>
              <div className="p-2 rounded bg-sky-950/30 border border-sky-500/30">
                <div className="text-sky-300/80">📦 Fletes Previos</div>
                <div className="font-bold text-sky-300">
                  {prevComp.length || tot.renglonesCompletadosPrevios || 0}
                </div>
              </div>
              <div className="p-2 rounded bg-slate-900/70 border border-slate-700">
                <div className="text-slate-400">🚫 No Aprobados</div>
                <div className="font-bold text-slate-300">
                  {cancMgmt.length || tot.renglonesCanceladosGerencia || 0}
                </div>
              </div>
            </div>

            {prevComp.length > 0 && (
              <div className="border border-sky-500/30 rounded bg-sky-950/15 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setShowPrevCompleted(prev => !prev)}
                  className="w-full px-2.5 py-1.5 flex items-center justify-between text-sky-300 font-semibold hover:bg-sky-950/30 transition"
                >
                  <span className="flex items-center gap-1.5">
                    <Package className="w-3.5 h-3.5 text-sky-400" />
                    <span>📦 {prevComp.length} insumo(s) recibidos al 100% en fletes anteriores</span>
                  </span>
                  <span className="text-[10px] underline">{showPrevCompleted ? 'Ocultar' : 'Ver'}</span>
                </button>
                {showPrevCompleted && (
                  <div className="p-2 border-t border-sky-500/20 space-y-1 font-mono text-[11px]">
                    {prevComp.map((cp: any, idx: number) => (
                      <div key={idx} className="flex justify-between text-slate-300">
                        <span>{cp.nombre}</span>
                        <span className="text-sky-300 font-bold">{cp.totalAcumulado ?? cp.cantidadRecibidaPrevia} / {cp.cantidadAprobada} ✓</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {cancMgmt.length > 0 && (
              <div className="border border-slate-700 rounded bg-slate-900/40 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setShowCanceledMgmt(prev => !prev)}
                  className="w-full px-2.5 py-1.5 flex items-center justify-between text-slate-300 font-semibold hover:bg-slate-800/50 transition"
                >
                  <span className="flex items-center gap-1.5">
                    <AlertOctagon className="w-3.5 h-3.5 text-rose-400/80" />
                    <span>🚫 {cancMgmt.length} insumo(s) no aprobados por Gerencia</span>
                  </span>
                  <span className="text-[10px] underline text-slate-400">{showCanceledMgmt ? 'Ocultar' : 'Ver'}</span>
                </button>
                {showCanceledMgmt && (
                  <div className="p-2 border-t border-slate-700/60 space-y-1 font-mono text-[11px]">
                    {cancMgmt.map((xc: any, idx: number) => (
                      <div key={idx} className="flex justify-between text-slate-400">
                        <span className="line-through">{xc.nombre}</span>
                        <span className="text-rose-300/80">Sol: {xc.cantidadSolicitada ?? 0} · Apr: 0</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Alerta de Bloqueo Financiero Preventivo (PLAN-040) */}
      {receptionComplete.hasFinancialBlock && (
        <div className="p-3 bg-red-950/40 border border-red-500/50 rounded-lg text-xs text-red-200 text-left space-y-1">
          <div className="flex items-center gap-2 font-bold text-red-400">
            <AlertOctagon className="w-4 h-4 text-red-400 shrink-0" />
            <span>🔒 ORDEN BLOQUEADA PREVENTIVAMENTE PARA PAGO</span>
          </div>
          <p className="text-[11px] text-red-300">
            Se detectó una desviación de costo unitario superior al 5% respecto a lo cotizado. El material fue ingresado físicamente a almacén para no detener la operación, pero la orden quedó congelada en Finanzas para auditoría de factura y se notificó al canal de Compras.
          </p>
        </div>
      )}

      {/* Opciones de Continuación para Sub-Órdenes Bifurcadas (PLAN-039 / D4) */}
      {subOrdenesList.length > 0 &&
        subOrdenesList.some(
          s => s.folio !== receptionComplete.folioOAB && s.estadoGeneral !== 'Completada'
        ) && (
          <div className="p-3 bg-purple-950/30 border border-purple-500/40 rounded-lg text-xs text-purple-200 text-left space-y-2">
            <p className="font-semibold flex items-center gap-1.5">
              <span>🔀 Sub-Órdenes adicionales pendientes en esta OAB:</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {subOrdenesList
                .filter(
                  s => s.folio !== receptionComplete.folioOAB && s.estadoGeneral !== 'Completada'
                )
                .map(rem => (
                  <button
                    key={rem.id}
                    type="button"
                    onClick={() => onSelectSubOrderFromSuccess(rem.folio, rem.numeroCotizacion)}
                    className="px-3 py-1.5 rounded bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 text-purple-300 font-mono text-xs font-bold transition flex items-center gap-1.5"
                  >
                    <span>
                      🚚 Recibir {rem.folio} ({rem.proveedor})
                    </span>
                  </button>
                ))}
            </div>
          </div>
        )}

      <div className="pt-2 flex flex-wrap justify-center gap-2.5">
        {/* Botón Imprimir Nota de Recepción Interna (NRI Carta) - PLAN-046 */}
        <button
          type="button"
          onClick={onPrintNriEntry}
          className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition shadow active:scale-95"
          title="Imprimir Nota de Recepción Interna (NRI Carta) con Sección A (Recibido) y Sección B (Saldos Pendientes)"
        >
          <Printer className="w-4 h-4" />
          <span>
            {nriPrinted
              ? '✅ Reimprimir Nota de Recepción (NRI Carta)'
              : 'Imprimir Nota de Recepción (NRI Carta)'}
          </span>
        </button>

        {/* Botón Imprimir Acta de Rechazo / Devolución - PLAN-046 */}
        {(itemsToReceive.some(it => (it.cantidadRechazada || 0) > 0) ||
          Boolean(receptionComplete.actaRechazoFolio)) && (
          <button
            type="button"
            onClick={onPrintRejectionAct}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-lg bg-red-600 hover:bg-red-500 text-white transition shadow active:scale-95"
            title="Imprimir Acta de No Conformidad y Rechazo legal para chofer y proveedor"
          >
            <FileText className="w-4 h-4" />
            <span>
              {rejectionPrinted ? '✅ Reimprimir Acta de Rechazo' : 'Imprimir Acta de Rechazo'}
            </span>
          </button>
        )}

        {/* Botón Ver en Historial NRI - PLAN-046 */}
        <button
          type="button"
          onClick={onOpenHistoryTab}
          className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-lg bg-slate-800 hover:bg-slate-700 text-emerald-300 border border-emerald-500/30 transition shadow active:scale-95"
        >
          <History className="w-4 h-4" />
          <span>📜 Ver en Historial NRI</span>
        </button>

        {/* Botón Asistido de Re-cotización por Quiebre de Proveedor (PLAN-040 / Decisión Q1) */}
        {(receptionComplete.reQuoteItems?.length > 0 ||
          itemsToReceive.some(it => it.actionOnRemaining === 'REQUOTE_ANOTHER_VENDOR')) && (
          <button
            type="button"
            onClick={() => {
              const fallbackItems = itemsToReceive
                .filter(it => it.actionOnRemaining === 'REQUOTE_ANOTHER_VENDOR')
                .map(l => {
                  const app = l.cantidadAprobada ?? l.cantidadSolicitada ?? 0;
                  const prev = l.cantidadRecibidaPrevia || 0;
                  const recHoy = l.cantidadRecibidaHoy ?? l.cantidadRecibida ?? 0;
                  const saldo = Math.max(0, app - prev - recHoy);
                  return {
                    ...l,
                    id: undefined,
                    solicitudId: undefined,
                    cantidadSolicitada: saldo,
                    cantidadAprobada: saldo,
                    cantidadRecibida: 0,
                    cantidadRecibidaHoy: 0,
                    cantidadRecibidaPrevia: 0,
                    cantidadRechazada: 0,
                    backorderPendiente: 0,
                    rotularEtiqueta: true,
                    notasDiscrepancia: `Re-cotización de saldo por quiebre en proveedor de ${
                      receptionComplete.folioOAB || selectedFolio
                    }`
                  };
                });

              const targetRequote =
                receptionComplete.reQuoteItems && receptionComplete.reQuoteItems.length > 0
                  ? receptionComplete.reQuoteItems
                  : fallbackItems;

              onTriggerRequote(targetRequote, receptionComplete.folioOAB || selectedFolio || 'OAB');
            }}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-lg bg-purple-600 hover:bg-purple-500 text-white transition shadow active:scale-95"
            title="Abrir modal de OAB en borrador con las líneas de saldo para cotizar con otro proveedor"
          >
            <RefreshCw className="w-4 h-4" />
            <span>
              Abrir Re-cotización en Borrador (
              {receptionComplete.reQuoteItems?.length ||
                itemsToReceive.filter(it => it.actionOnRemaining === 'REQUOTE_ANOTHER_VENDOR')
                  .length}
              )
            </span>
          </button>
        )}

        {labelsToPrint.length > 0 && (
          <button
            type="button"
            onClick={onOpenLabelModal}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-950 transition shadow active:scale-95"
          >
            <Tag className="w-4 h-4" />
            <span>
              Etiquetas de Proyecto ({labelsToPrint.reduce((acc, l) => acc + l.bultos, 0)})
            </span>
          </button>
        )}
        <button
          onClick={onClose}
          className="px-5 py-2 text-xs font-semibold rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-100 transition"
        >
          Cerrar Terminal
        </button>
      </div>

      {/* Panel Semiautomático de Notificación Multicanal Etapa 4 (PLAN-046 / PLAN-047 / PLAN-048) */}
      {receptionComplete.status !== 'queued_offline' && (
        <div className="pt-2 text-left">
          <OABStageNotifyPanel
            stage="RAMP_RECEPTION"
            folio={receptionComplete.folioOAB || selectedFolio || 'OAB-RAMPA'}
            linesCount={itemsToReceive.length}
            storesSummary={resolvedStoresSummary}
            vendorName={activeVendor}
            rampSummary={{
              nriFolio: receptionComplete.nriFolio || 'NRI-RECIENTE',
              actaRechazoFolio: receptionComplete.actaRechazoFolio || null,
              proveedor: activeVendor,
              numeroNotaEntrega: notaEntrega,
              choferNombre: choferNombre || receptionComplete.nriSnapshot?.choferNombre || undefined,
              placaVehiculo: placaVehiculo || receptionComplete.nriSnapshot?.placaVehiculo || undefined,
              nriPrinted,
              rejectionPrinted,
              arrivedItems: (
                receptionComplete.nriSnapshot?.itemsRecibidos ||
                itemsToReceive.filter(it => (it.cantidadRecibidaHoy ?? it.cantidadRecibida ?? 0) > 0)
              ).map((it: any) => ({
                nombre: it.nombre,
                codigo: it.codigo,
                cantidad: Number(it.cantidadRecibidaHoy ?? it.cantidadRecibida ?? it.cantidad ?? 0),
                unidad: it.unidadMedida || it.unidad || 'und',
                ubicacion: it.ubicacionBodega || it.ubicacion || 'Por Ubicar'
              })),
              missingItems: (
                receptionComplete.nriSnapshot?.itemsFaltantes ||
                itemsToReceive
                  .map(it => {
                    const app = it.cantidadAprobada ?? it.cantidadSolicitada ?? 0;
                    const prev = it.cantidadRecibidaPrevia || 0;
                    const recHoy = it.cantidadRecibidaHoy ?? it.cantidadRecibida ?? 0;
                    const saldo = Math.max(0, app - prev - recHoy);
                    return {
                      nombre: it.nombre,
                      codigo: it.codigo,
                      saldoPendiente: saldo,
                      unidadMedida: it.unidadMedida || 'und',
                      actionOnRemaining: it.actionOnRemaining || 'BACKORDER'
                    };
                  })
                  .filter(it => it.saldoPendiente > 0 && it.actionOnRemaining !== 'DISCARD')
              ).map((it: any) => ({
                nombre: it.nombre,
                codigo: it.codigo,
                cantidadFaltante: Number(it.saldoPendiente ?? it.cantidadFaltante ?? 0),
                unidad: it.unidadMedida || it.unidad || 'und'
              })),
              rejectedItems: (
                receptionComplete.nriSnapshot?.itemsRechazados ||
                itemsToReceive.filter(it => (it.cantidadRechazada || 0) > 0)
              ).map((it: any) => ({
                nombre: it.nombre,
                codigo: it.codigo,
                cantidadRechazada: Number(it.cantidadRechazada || 0),
                unidad: it.unidadMedida || it.unidad || 'und',
                motivo: it.motivo || it.notasDiscrepancia || 'Rechazado en inspección física en rampa'
              }))
            }}
          />
        </div>
      )}
    </div>
  );
};
